import React from 'react'
import { Pagination } from '@mui/material'
import { DataGrid, useGridApiContext } from '@mui/x-data-grid'
import PropTypes from 'prop-types'

/** @type {import('@mui/x-data-grid').GridColDef} */
export const DEF_COL_DEFS = {
  flex: 1,
  disableReorder: true
}

/**
 * @typedef {Object} DataGridWrapperProps
 * @prop {string} [tableHeight]
 * @prop {number} [pageCount]
 * @prop {number} [pageNumber] // Added pageNumber
 *
 * @prop {import('@mui/x-data-grid').GridColDef[]} columns
 * @prop {import('@mui/x-data-grid').GridRowsProp} rows
 */

/** @type {import('react').CSSProperties & Object.<string, import('react').CSSProperties>} */
const gridStyles = {
  '.MuiDataGrid-columnHeaderTitle': {
    fontWeight: 'bolder',
    fontSize: '12px'
  },
  '.MuiDataGrid-cellContent': {
    fontSize: '12px'
  },
  border: 'none'
}

/**
 * Custom Pagination component.
 * @param {{ pageCount: number, pageNumber: number, onPageChange: Function }} props
 */
function CustomPagination({ pageCount, pageNumber, onPageChange }) {
  const apiRef = useGridApiContext()

  return (
    <Pagination
      color='primary'
      count={pageCount}
      page={pageNumber} // Use pageNumber prop
      onChange={(_event, value) => {
        apiRef.current.setPage(value - 1)
        onPageChange(value)
      }}
    />
  )
}

CustomPagination.propTypes = {
  pageCount: PropTypes.number.isRequired,
  pageNumber: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired
}

/**
 * @param {import('@mui/x-data-grid').DataGridProps & DataGridWrapperProps} props
 */
function MUIDataGrid(props) {
  const {
    tableHeight = '500px',
    pageCount = 0,
    pageNumber = 1, // Default to page 1
    onPageChange,
    ...dataGridProps
  } = props
  const { sx, initialState, components, ...miscDgProps } = dataGridProps

  const mappedColumns = dataGridProps.columns.map((c) => ({
    ...DEF_COL_DEFS,
    ...c
  }))

  return (
    <div style={{ height: tableHeight, width: '100%' }}>
      <div style={{ display: 'flex', height: '100%' }}>
        <div style={{ flexGrow: 1 }}>
          <DataGrid
            sx={{ ...gridStyles, ...sx }}
            disableColumnMenu
            initialState={{
              pagination: {
                pageSize: dataGridProps.pageSize
              },
              ...initialState
            }}
            components={{
              Pagination: (props) => (
                <CustomPagination
                  {...props}
                  pageCount={pageCount}
                  pageNumber={pageNumber} // Pass pageNumber
                  onPageChange={onPageChange}
                />
              ),
              ...components
            }}
            {...miscDgProps}
            columns={mappedColumns}
          />
        </div>
      </div>
    </div>
  )
}

MUIDataGrid.propTypes = {
  tableHeight: PropTypes.string,
  pageCount: PropTypes.number,
  pageNumber: PropTypes.number, // Prop added
  onPageChange: PropTypes.func.isRequired
}

export default MUIDataGrid
